import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { t } from "@/lib/i18n";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { LogIn, Loader2 } from "lucide-react";

const AuthPage = ({ mode }) => {
  const { language, login } = useApp();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);

  const isSignup = mode === "signup";
  const lang = language || "en";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const path = isSignup ? "/auth/signup" : "/auth/login";
      const payload = isSignup ? form : { email: form.email, password: form.password };
      const { data } = await api.post(path, payload);
      login(data.token, data.user);
      toast.success(isSignup ? "Account created" : "Welcome back");
      navigate("/category");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t(lang, "errorGeneric"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <main className="max-w-md mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="bg-white border border-slate-200 rounded-2xl p-8"
        >
          <div className="flex items-center gap-2 mb-6">
            <div className="h-10 w-10 rounded-lg bg-[#006C35]/10 text-[#006C35] flex items-center justify-center">
              <LogIn className="h-5 w-5" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">
              {isSignup ? t(lang, "signup") : t(lang, "login")}
            </h1>
          </div>

          <form onSubmit={submit} className="space-y-4">
            {isSignup && (
              <div>
                <Label htmlFor="name" className="text-start block mb-1.5">{t(lang, "name")}</Label>
                <Input
                  data-testid="auth-name-input"
                  id="name" required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-start block mb-1.5">{t(lang, "email")}</Label>
              <Input
                data-testid="auth-email-input"
                id="email" type="email" required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-start block mb-1.5">{t(lang, "password")}</Label>
              <Input
                data-testid="auth-password-input"
                id="password" type="password" required minLength={6}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            </div>
            <Button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-[#006C35] hover:bg-[#004F27] text-white"
            >
              {loading && <Loader2 className="h-4 w-4 me-2 animate-spin" />}
              {isSignup ? t(lang, "signup") : t(lang, "login")}
            </Button>
          </form>

          <div className="text-center text-sm text-slate-500 mt-6">
            {isSignup ? t(lang, "haveAccount") : t(lang, "noAccount")}{" "}
            <Link
              data-testid={isSignup ? "auth-goto-login" : "auth-goto-signup"}
              to={isSignup ? "/login" : "/signup"}
              className="text-[#006C35] font-medium hover:underline"
            >
              {isSignup ? t(lang, "login") : t(lang, "signup")}
            </Link>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default AuthPage;
